<template>
	<div class="calendar-wrap">
		<CalendarStepper :value="calendarValue" @change="calendarStepperChange" large>
			<template #default>
				<span class="calendar-stepper-span">{{ dateFormatYearMonth(calendarValue, '.') }}</span>
			</template>
		</CalendarStepper>
		<el-calendar
			ref="calendarRef"
			v-model="calendarValue"
			:first-day-of-week="7"
			type="card"
			operations="click,today,prev-month,next-month"
			hide-header
			style="margin-top: 12px"
			@cell-click="cardCellClick"
		>
			<template slot="theadCell" slot-scope="{ value }">
				<span>{{ formatWeek(value) }}</span>
			</template>

			<template slot="dateCell" slot-scope="{ date, data }">
				<span v-if="isToday(date)" class="card-today-cell">{{ formatCellDay(data.day) }}</span>
				<span v-else class="card-day-date">{{ formatCellDay(data.day) }}</span>
				<!-- 日历圆点样式 -->
				<div class="card-day-cell-count">{{ getCellCount(date) }}</div>
			</template>
		</el-calendar>
	</div>
</template>

<script setup>
import { ref, computed } from 'vue';
import CalendarStepper from '@/components/calendar/calendar-stepper.vue';
import i18n, { EN } from '@/locales';
import moment from 'moment';
import { dateFormatYearMonth } from '@/utils/format';
import { useScheduleCore } from '@/hooks/calendar/useScheduleCore';

const { calculateDateRange, filterDataByTimeRange } = useScheduleCore();

const cacheNow = moment().startOf('day'); // 获取今天的开始时间
const isToday = date => {
	const then = moment(date); // 转化输入的日期
	return cacheNow.isSame(then, 'd'); // 比较两者是否在同一天
};

const formatCellDay = day => {
	return moment(day).format('D');
};

const formatWeek = val => {
	const weekMap = {
		'一': 'M',
		'二': 'T',
		'三': 'W',
		'四': 'T',
		'五': 'F',
		'六': 'S',
		'日': 'S'
	};
	if (i18n.locale === EN) {
		return weekMap[val];
	}
	// 繁体中文字相同
	return val;
};

const props = defineProps({
	value: {
		type: Date
	},
	scheduleData: {
		type: Array,
		default: () => []
	}
});

const getterScheduleData = computed(() => props.scheduleData);
const getCellCount = date => {
	const range = calculateDateRange(date, 'day');
	const rangeResult = filterDataByTimeRange(range, getterScheduleData.value);
	return rangeResult?.length ? `${rangeResult?.length}` : '-';
};

const emits = defineEmits(['update:value', 'change', 'cell-click']);
const calendarValue = computed({
	get: () => {
		if (!props.value) return new Date();
		return props.value;
	},
	set: val => {
		emits('update:value', val);
	}
});

const activeView = ref('month');
const calendarRef = ref();
const stepperTypeMapByMonth = {
	prev: 'prev-month',
	next: 'next-month',
	today: 'today'
};
const calendarStepperChange = type => {
	if (activeView.value === 'month') calendarRef.value && calendarRef.value.selectDate(stepperTypeMapByMonth[type]);
	else calendarRef.value && calendarRef.value.selectDate(type);
};

const openCellDialog = value => {
	emits('cell-click', {
		date: value,
		view: activeView.value
	});
};
const cardCellClick = value => {
	openCellDialog(value);
};
</script>

<style lang="less" scoped>
.calendar-wrap {
	padding: 20px 10px 10px;
	display: flex;
	flex-direction: column;

	/deep/.calendar-stepper-wrapper {
		justify-content: center;
		gap: 24px;
	}
	.calendar-stepper-span {
		color: var(--surf-inverse-color);
		text-align: center;
		font-family: D-DIN;
		font-size: 32px;
		font-weight: 700;
		line-height: 40px; /* 125% */
	}
	/deep/.svg-icon {
		border-radius: 100% !important;
		background: rgba(var(--surf-inverse-color-rgb), 0.03);
	}
	/deep/.el-calendar {
		background: var(--surf-secondary-color);
		.el-calendar__body {
			border: none;
			.el-calendar__thead_box {
				box-shadow: none;
			}
			.el-calendar-table {
				thead {
					background: var(--surf-secondary-color);
				}
				tr {
					gap: 16px;
				}
				.el-calendar-day {
					display: flex;
					flex-direction: column;
					justify-content: center;
					width:100%;
					height: 64px;
					border-radius: 8px;
					background: rgba(var(--surf-inverse-color-rgb), 0.1);
					.card-day-date,
					.card-today-cell {
						color: var(--surf-inverse-color);
						font-family: D-DIN;
						font-size: 24px;
						font-weight: 700;
						line-height: 32px; /* 133.333% */
					}
					.card-day-cell-count {
						color: rgba(var(--surf-inverse-color-rgb), 0.4);
						font-size: 12px;
						font-weight: 400;
						line-height: 16px; /* 133.333% */
					}
				}
				.is-today .el-calendar-day::before {
					border-color: var(--surf-inverse-color);
					border-radius: 8px;
				}
			}
			.prev .el-calendar-day,
			.next .el-calendar-day {
				background: transparent;
				border: 0.5px solid rgba(var(--surf-inverse-color-rgb), 0.1);
			}
			.is-selected .el-calendar-day {
				background: var(--surf-inverse-color) !important;
				.card-day-date,
				.card-today-cell {
					color: rgba(var(--surf-theme-color-rgb), 1);
				}
				.card-day-cell-count {
					color: rgba(var(--surf-theme-color-rgb), 0.4);
				}
			}
		}
	}
}
</style>
